import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { Handlers } from "./types";

/**
 * UniversalEntityPageHandlersContext
 *
 * Contains all event handlers:
 * - CRUD handlers (create, edit, delete, view)
 * - Lifecycle handlers (itemCreated, itemUpdated, itemDeleted)
 * - Import/Export handlers
 * - Modal handlers (compose, detail)
 *
 * Separated from other contexts to prevent unnecessary re-renders
 * when data or UI state changes.
 */
export type UniversalEntityPageHandlersContextValue<T extends BaseEntity> = Handlers<T>;

const UniversalEntityPageHandlersContext = createContext<
  UniversalEntityPageHandlersContextValue<any> | undefined
>(undefined);

export function UniversalEntityPageHandlersProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageHandlersContextValue<T>;
}) {
  return (
    <UniversalEntityPageHandlersContext.Provider value={value}>
      {children}
    </UniversalEntityPageHandlersContext.Provider>
  );
}

export function useUniversalEntityPageHandlersContext<T extends BaseEntity>() {
  const context = useContext(UniversalEntityPageHandlersContext);
  if (!context) {
    throw new Error(
      "useUniversalEntityPageHandlersContext must be used within UniversalEntityPageHandlersProvider"
    );
  }
  return context as UniversalEntityPageHandlersContextValue<T>;
}
